import { INoteObservable } from "@/store/note/types";
import { TemplateObservable } from "@/store/templates/TemplateObservable";
import { NoteObservable } from "@/store/note/NoteObservable";
import { MemCommonEditorStore } from "@/components/note/store/mem-common-editor-store";
import { NoteEditorMode } from "@/components/note/types";

type NotesViewPageLike =
  | {
      noteVersionHistory?: {
        currentNoteId?: string | null;
      };
    }
  | undefined;

interface UseNoteEditorDisplayStateArgs<
  TNoteObservable extends INoteObservable | TemplateObservable,
> {
  noteObservable: TNoteObservable;
  editorStore?: MemCommonEditorStore;
  mode: NoteEditorMode;
  notesViewPage?: NotesViewPageLike;
}

export interface NoteEditorDisplayState {
  shouldShowBottomBar: boolean;
  shouldShowQuickActions: boolean;
}

export const useNoteEditorDisplayState = <
  TNoteObservable extends INoteObservable | TemplateObservable,
>({
  noteObservable,
  editorStore,
  mode,
  notesViewPage,
}: UseNoteEditorDisplayStateArgs<TNoteObservable>): NoteEditorDisplayState => {
  const isNoteObservable = noteObservable instanceof NoteObservable;
  const hasSupportedSources = isNoteObservable
    ? (noteObservable.sourceList?.allSupportedSources?.length ?? 0) > 0
    : false;

  const isViewingVersionHistory =
    notesViewPage?.noteVersionHistory?.currentNoteId === noteObservable.id;

  const isNoteEmpty = editorStore?.isNoteEmpty ?? false;

  const shouldShowBottomBar =
    isNoteObservable &&
    mode === NoteEditorMode.Editable &&
    !isViewingVersionHistory &&
    (hasSupportedSources || !isNoteEmpty);

  const shouldShowQuickActions = isNoteEmpty && !shouldShowBottomBar;

  return {
    shouldShowBottomBar,
    shouldShowQuickActions,
  };
};
